import React, { useState, useEffect, useContext } from 'react';
import { ConnectPayouts } from '@stripe/react-connect-js';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';
import { CCard, CCardBody, CCardHeader, CSpinner } from '@coreui/react';
import { AuthUserContext } from '../../../../session';
import Nav from '../../CreatorComponents/Nav';
import { getDatabase, ref, get, query, orderByChild, equalTo } from 'firebase/database';
import { useStripeConnect } from '../../CreatorComponents/useStripeConnect';

const Payouts = ({ stripeAccountId: stripeAccountIdProp }) => {
  const [loading, setLoading] = useState(true);
  const [stripeAccountId, setStripeAccountId] = useState(stripeAccountIdProp || null);
  const [stripeError, setStripeError] = useState('');
  const authContext = useContext(AuthUserContext);
  const authUser = authContext?.authUser || authContext;
  const { hasError, stripeConnectInstance } = useStripeConnect(stripeAccountId);

  useEffect(() => {
    let mounted = true;

    const resolveStripeAccount = async () => {
      try {
        if (stripeAccountIdProp) {
          if (mounted) setStripeAccountId(stripeAccountIdProp);
          return;
        }

        const existing =
          authUser?.stripeAccountId ||
          authUser?.stripeAccount?.id ||
          authUser?.authUser?.stripeAccountId ||
          null;

        if (existing) {
          if (mounted) setStripeAccountId(existing);
          return;
        }

        const email = authUser?.email || authUser?.authUser?.email;
        if (!email) {
          if (mounted) setStripeError('No authenticated creator email found.');
          return;
        }

        const db = getDatabase();
        const creatorsRef = ref(db, 'verified_creators');
        const emailQuery = query(creatorsRef, orderByChild('email'), equalTo(email));
        const snapshot = await get(emailQuery);

        if (snapshot.exists() && mounted) {
          const creatorData = Object.values(snapshot.val())[0];
          setStripeAccountId(creatorData?.stripeAccountId || null);
        } else if (mounted) {
          setStripeError('No Stripe account found for this creator.');
        }
      } catch (error) {
        console.error('Failed to resolve Stripe account:', error);
        if (mounted) setStripeError(error.message || 'Failed to load creator account');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    resolveStripeAccount();

    return () => {
      mounted = false;
    };
  }, [authUser, stripeAccountIdProp]);

  if (stripeError || hasError) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Nav />
        <div style={{ padding: '20px' }}>
          <CCard className="mb-4">
            <CCardBody>
              <div style={{ padding: '16px', color: 'red' }}>
                Error loading Stripe: {stripeError || 'Stripe Connect failed to initialize.'}
              </div>
            </CCardBody>
          </CCard>
        </div>
      </div>
    );
  }

  if (loading || !stripeAccountId || !stripeConnectInstance) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Nav />
        <div style={{ 
          padding: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <CSpinner />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Nav />
      <div style={{ padding: '20px' }}>
        <CCard className="mb-4">
          <CCardHeader style={{ 
            backgroundColor: '#187da0', 
            color: 'white',
            padding: '20px'
          }}>
            <h1 style={{ margin: 0, fontSize: '24px' }}>Payouts</h1>
          </CCardHeader>
          <CCardBody>
            <div style={{ padding: '16px' }}>
              <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>Recent Payouts</h2>
              <ConnectComponentsProvider 
                connectInstance={stripeConnectInstance}
                publishableKey={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY}
                account={stripeAccountId}
                options={{ stripeAccount: stripeAccountId }}
              >
                <ConnectPayouts />
              </ConnectComponentsProvider>
            </div>
          </CCardBody>
        </CCard>
      </div>
    </div>
  );
};

export default Payouts; 
